// File: frontend/src/components/BusinessList.js
import React, { useState } from 'react';
import { Building2, Search, Plus, Edit2, Trash2, Users, MapPin, Phone, Mail, Globe, User, Eye, Grid3x3, Table } from 'lucide-react';
import { businessAPI } from '../utils/api';
import { BUSINESS_STATUSES, getBusinessPipelineStatus, getBusinessStatusColor, getBusinessStatusLabel } from '../utils/businessStatus';
import BusinessDetailModal from './BusinessDetailModal';

const BusinessList = ({
  businesses,
  fetchBusinesses,
  setShowAddBusinessForm,
  setEditingBusiness,
  mode = 'active',
  title = 'Business Management',
  emptyMessage = 'No businesses found matching your search criteria.',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const visibleBusinesses = businesses.filter(business => {
    const normalizedStatus = getBusinessPipelineStatus(business);
    if (mode === 'not_relevant') {
      return normalizedStatus === 'not_relevant';
    }
    if (mode === 'to_review') {
      return normalizedStatus === 'to_review';
    }
    return normalizedStatus !== 'not_relevant' && normalizedStatus !== 'to_review';
  });

  const filteredBusinesses = visibleBusinesses.filter(business => {
    const matchesSearch = searchTerm === '' || 
      business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (business.owner_first_name && business.owner_last_name && 
        `${business.owner_first_name} ${business.owner_last_name}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (business.owner_name && business.owner_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (business.city && business.city.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesIndustry = filterIndustry === '' || business.industry === filterIndustry;
    const matchesStatus = filterStatus === '' || getBusinessPipelineStatus(business) === filterStatus;
    
    return matchesSearch && matchesIndustry && matchesStatus;
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this business?')) {
      try {
        await businessAPI.delete(id);
        fetchBusinesses();
      } catch (error) {
        console.error('Error deleting business:', error);
        alert('Failed to delete business');
      }
    }
  };

  const getStatusColor = (status) => {
    return getBusinessStatusColor(status);
  };

  const getLocation = (business) => [business.city, business.state, business.country].filter(Boolean).join(', ');
  const getOwner = (business) => business.owner_name || [business.owner_first_name, business.owner_last_name].filter(Boolean).join(' ');
  const formatStatus = getBusinessStatusLabel;

  const handleEdit = (business) => {
    setSelectedBusiness(null);
    setEditingBusiness(business);
  };

  const uniqueIndustries = [...new Set(visibleBusinesses.map(b => b.industry).filter(Boolean))];
  const statusOptions = mode === 'not_relevant'
    ? BUSINESS_STATUSES.filter(status => status.value === 'not_relevant')
    : mode === 'to_review'
      ? BUSINESS_STATUSES.filter(status => status.value === 'to_review')
      : BUSINESS_STATUSES.filter(status => status.value !== 'not_relevant' && status.value !== 'to_review');

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent dark:from-gray-100 dark:to-gray-300">{title}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-accent-primary" />
            {filteredBusinesses.length === visibleBusinesses.length ? (
              <span className="font-medium">{visibleBusinesses.length} businesses</span>
            ) : (
              <span className="font-medium">{filteredBusinesses.length} of {visibleBusinesses.length} businesses</span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded flex items-center space-x-2 transition ${
                viewMode === 'cards' ? 'bg-white dark:bg-blue-600 shadow-sm text-blue-600 dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
              title="Card View"
            >
              <Grid3x3 className="w-4 h-4" />
              <span className="text-sm font-medium hidden md:inline">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded flex items-center space-x-2 transition ${
                viewMode === 'table' ? 'bg-white dark:bg-blue-600 shadow-sm text-blue-600 dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
              title="Table View"
            >
              <Table className="w-4 h-4" />
              <span className="text-sm font-medium hidden md:inline">Table</span>
            </button>
          </div>

          <button
            onClick={() => setShowAddBusinessForm(true)}
            className="px-6 py-3 bg-gradient-primary text-white rounded-glass hover:shadow-glow-md transition-all duration-300 flex items-center group"
          >
            <Plus className="w-5 h-5 mr-2 group-hover:animate-pulse" />
            Add Business
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent-primary w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, owner, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 glass border border-white/30 rounded-glass focus:outline-none focus:border-accent-primary focus:shadow-glow-sm transition-all duration-300 dark:bg-slate-800 dark:text-white dark:placeholder-gray-500"
              />
            </div>
          </div>
          <select
            value={filterIndustry}
            onChange={(e) => setFilterIndustry(e.target.value)}
            className="px-4 py-3 glass border border-white/30 rounded-glass focus:outline-none focus:border-accent-primary focus:shadow-glow-sm transition-all duration-300 dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Industries</option>
            {uniqueIndustries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 glass border border-white/30 rounded-glass focus:outline-none focus:border-accent-primary focus:shadow-glow-sm transition-all duration-300 dark:bg-slate-800 dark:text-white"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="glass-card backdrop-blur-xl border border-white/30 shadow-glass-lg rounded-glass-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50/80 dark:bg-slate-800/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Industry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contacts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white/70 dark:bg-slate-900/70 divide-y divide-gray-200 dark:divide-slate-700">
                {filteredBusinesses.map(business => (
                  <tr
                    key={business.id}
                    className="hover:bg-blue-50/70 dark:hover:bg-slate-800/80 cursor-pointer transition-colors"
                    onClick={() => setSelectedBusiness(business)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-glass bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-green-600 dark:text-green-300" />
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white break-words">{business.name}</div>
                          {business.type && <div className="text-sm text-gray-500 dark:text-gray-400">{business.type}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{business.industry || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{getOwner(business) || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{getLocation(business) || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-300">
                        {business.phone && <Phone className="w-4 h-4" />}
                        {business.email && <Mail className="w-4 h-4" />}
                        {business.website && <Globe className="w-4 h-4" />}
                        {!business.phone && !business.email && !business.website && <span className="text-sm text-gray-400">-</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {business ? (
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(business)}`}>
                          {formatStatus(business)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedBusiness(business);
                          }}
                          className="p-2 rounded-glass text-accent-primary hover:bg-blue-600 hover:text-white transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEdit(business);
                          }}
                          className="p-2 rounded-glass text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(business.id);
                          }}
                          className="p-2 rounded-glass text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBusinesses.map(business => (
            <div key={business.id} className="glass-card backdrop-blur-xl border border-white/30 shadow-glass-lg rounded-glass-lg p-6 hover:shadow-glass-xl transition-all duration-300 group">
              <div className="flex justify-between items-start mb-4">
                <button
                  type="button"
                  onClick={() => setSelectedBusiness(business)}
                  className="flex items-center space-x-3 min-w-0 text-left"
                >
                  <div className="w-12 h-12 rounded-glass bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-green-600 dark:text-green-300" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-words">{business.name}</h3>
                    {business.type && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{business.type}</p>
                    )}
                  </div>
                </button>
                <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <button
                    onClick={() => setSelectedBusiness(business)}
                    className="p-2 glass-button rounded-glass text-accent-primary hover:bg-gradient-primary hover:text-white transition-all duration-300"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(business)}
                    className="p-2 glass-button rounded-glass text-gray-600 dark:text-gray-300 hover:bg-gradient-secondary hover:text-white dark:hover:text-white transition-all duration-300"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(business.id)}
                    className="p-2 glass-button rounded-glass text-accent-danger hover:bg-gradient-danger hover:text-white transition-all duration-300"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {business.industry && (
                  <div className="flex items-center text-sm">
                    <Building2 className="w-4 h-4 mr-2 text-accent-secondary" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{business.industry}</span>
                  </div>
                )}

                {(business.owner_first_name || business.owner_last_name) && (
                  <div className="flex items-center text-sm">
                    <User className="w-4 h-4 mr-2 text-accent-tertiary" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      Owner: {getOwner(business)}
                    </span>
                  </div>
                )}

                {getLocation(business) && (
                  <div className="flex items-center text-sm">
                    <MapPin className="w-4 h-4 mr-2 text-accent-primary" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {getLocation(business)}
                    </span>
                  </div>
                )}

                {business.employee_count > 0 && (
                  <div className="flex items-center text-sm">
                    <Users className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-500" />
                    <span className="text-gray-600 dark:text-gray-400">{business.employee_count} employees</span>
                  </div>
                )}

                <div className="flex items-center space-x-3 text-sm">
                  {business.phone && (
                    <a href={`tel:${business.phone}`} className="text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200">
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                  {business.email && (
                    <a href={`mailto:${business.email}`} className="text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200">
                      <Mail className="w-4 h-4" />
                    </a>
                  )}
                  {business.website && (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200">
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>

              {business && (
                <div className="mt-4">
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-glass ${getStatusColor(business)}`}>
                    {formatStatus(business)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredBusinesses.length === 0 && (
        <div className="text-center py-12">
          <div className="glass-card backdrop-blur-xl border border-white/30 shadow-glass-lg rounded-glass-lg p-8 max-w-md mx-auto">
            <Building2 className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 font-medium">{emptyMessage}</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Try adjusting your filters or search terms.</p>
          </div>
        </div>
      )}

      <BusinessDetailModal
        business={selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default BusinessList;
